import { FunctionComponent, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { injectReducerAsync } from 'app/reduxStore';
import {
  defaultCurrency,
  defaultFinancialsMagnitude,
  getProject,
  getProjectSource,
  isLiveLinkingEnabled,
} from 'features/project/projectSlice';
import FederatedMFE from 'common/components/FederatedMFE';
import featureFlags, {
  isDisplayFormatEnabled,
  isSVGminitableIngestionEnabled,
  isInlineCommentingEnabled,
  inlineCommentingEnabledForStructuredAnalysisMfe,
} from 'common/helpers/featureFlags';
import Footer from 'common/components/Footer/Footer';
import {
  getFakeClientUser,
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';
import {
  isProjectArchived,
  isProjectInactive,
} from 'common/helpers/projectStatusesUtils';
import { useHistory } from 'react-router-dom';

const StructuredAnalysisMFE: FunctionComponent = () => {
  const project = useSelector(getProject);
  const isLiveLinking = useSelector(isLiveLinkingEnabled);
  const projectSource = useSelector(getProjectSource);
  const userRole = useSelector(getUserRole);
  const isFakeClient = useSelector(getFakeClientUser);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const history = useHistory();

  const extraProps = useMemo(
    () => ({
      userInfo: {
        userRole,
        isFakeClient,
        isThirdPartyMember,
      },
      projectInfo: {
        ...project,
        isProjectArchived: isProjectArchived(project?.status),
        isProjectInactive: isProjectInactive(project?.status),
        defaultCurrency,
        defaultFinancialsMagnitude,
        financialsDecimalCount: project.financialsDecimalCount,
        isLiveLinkingEnabled: isLiveLinking,
        ManualRefreshEnabled: project.ManualRefreshEnabled,
        projectSource,
      },
      flags: {
        isStructuredAnalysesTitleEditable:
          featureFlags?.isStructuredAnalysesTitleEditable,
        isAutoSaveEnabled: featureFlags?.isAutoSaveEnabled,
        isAutoSaveDevMode: featureFlags?.isAutoSaveDevMode,
        isDownloadExcelTableEnabled: featureFlags?.downloadExcelTableEnabled,
        isInlineCommentingEnabled:
          isInlineCommentingEnabled &&
          inlineCommentingEnabledForStructuredAnalysisMfe &&
          !isThirdPartyMember,
        isSVGminitableIngestionEnabled,
        isWorksheetIngestionEnabled: featureFlags?.isWorksheetIngestionEnabled,
        isDisplayFormatEnabled,
        downloadExcelTableEnabled: featureFlags?.downloadExcelTableEnabled,
        isStructuredAnalysisLivePreviewEnabled:
          featureFlags?.isStructuredAnalysisLivePreviewEnabled,
        isImageUploadEnabled: featureFlags?.isImageUploadEnabled,
        isLRPLoaderFlagEnabled: featureFlags?.isLRPLoaderFlagEnabled,
        isLanguageSelectionEnabled: featureFlags?.isLanguageSelectionEnabled,
      },
      envs: {
        STRUCTURED_ANALYSIS_SERVICE_API_BASE_PATH:
          process.env.REACT_APP_STRUCTURED_ANALYSIS_SERVICE,
        CONNECTED_CONTENT_API_BASE_PATH:
          process.env.REACT_APP_CONNECTED_CONTENT_API_BASE_PATH,
        CONTENT_SERVICE_API_BASE_PATH: process.env.REACT_APP_CONTENT_SERVICE,
        LOCK_API_BASE_PATH: process.env.REACT_APP_LOCK_API_BASE_PATH,
        PROJECT_API_BASE_PATH: process.env.REACT_APP_PROJECT_API_BASE_PATH,
        REPORTING_API_BASE_PATH: process.env.REACT_APP_REPORTING_SERVICE,
        TAGS_API_BASE_PATH: process.env.REACT_APP_TAGS_LIST_SERVICE,
        LIBRARY_ITEM_API_BASE_PATH:
          process.env.REACT_APP_CLIPBOARD_LIBRARY_ITEM_SERVICE,
        LIBRARY_ITEM_EXCEL_API_BASE_PATH:
          process.env.REACT_APP_EXCEL_LIBRARY_ITEM_SERVICE,
        LIBRARY_ITEM_POWER_POINT_API_BASE_PATH:
          process.env.REACT_APP_PPT_LIBRARY_ITEM_SERVICE,
        LIBRARY_ITEM_RICH_TEXT_API_BASE_PATH:
          process.env.REACT_APP_RICH_TEXT_LIBRARY_ITEM_SERVICE,
        COMMENT_SERVICE_BASE_PATH: process.env.REACT_APP_COMMENT_SERVICE,
        REACT_APP_IMAGE_API_BASE_PATH: process.env.REACT_APP_IMAGE_API,
        AUTO_SAVE_IDLE_TIME: process.env.REACT_APP_AUTO_SAVE_IDLE_TIME,
        AUTO_SAVE_DELAY: process.env.REACT_APP_AUTO_SAVE_DELAY,
        AUTO_LOCK_DELAY: process.env.REACT_APP_AUTO_LOCK_DELAY,
        TERRITORY: process.env.REACT_APP_TERRITORY,
        VERSIONING_MFE_URL: process.env.REACT_APP_VERSIONING_MFE_URL,
        COMMENTING_IDLE_TIMEOUT: Number(
          process.env.REACT_APP_COMMENTING_IDLE_TIMEOUT
        ),
        COMMENTING_SIDEBAR_MFE_URL:
          process.env.REACT_APP_COMMENTING_SIDEBAR_MFE_URL,
        COMMENTING_CONTROLLER_MFE_URL:
          process.env.REACT_APP_COMMENTING_CONTROLLER_MFE_URL,
        CK_EDITOR_LICENSE: process.env.REACT_APP_CK_EDITOR_LICENSE,
        CLIPBOARD_LIBRARY_ITEM_API_BASE_PATH:
          process.env.REACT_APP_CLIPBOARD_LIBRARY_ITEM_SERVICE,
        LIVE_REPORT_PREVIEW_MFE_URL:
          process.env.REACT_APP_LIVE_REPORT_PREVIEW_MFE_URL,
      },
      callbacks: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigateTo: (url: string, params: any) => {
          history.push(url, params);
        },
      },
      FooterHostComponent: <Footer />,
    }),
    [
      userRole,
      project,
      isLiveLinking,
      projectSource,
      history,
      isFakeClient,
      isThirdPartyMember,
    ]
  );

  return (
    <FederatedMFE
      urlKey="REACT_APP_STRUCTURED_ANALYSIS_MFE_URL"
      scope="remoteapp"
      componentModule="./StructuredAnalysis"
      injectionReducerCb={injectReducerAsync}
      props={extraProps}
    />
  );
};

export default StructuredAnalysisMFE;
